import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { ScopeSelector } from "@packages/core-client/scope/ScopeSelector.tsx";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAnalyticsScopeClient } from "../api/query.ts";
import { useAnalyticsTracking } from "../hooks/useAnalyticsTracking.ts";
import type { ReportingScope } from "@packages/core/organization/scope.ts";
import { type FeedbackPeriod, getFeedbackDataFloor } from "../hooks/feedbackPeriod.ts";
import { useFeedbackPeriod, useFeedbackScope } from "../hooks/useFeedbackFilters.ts";
import { FeedbackPeriodToolbar } from "./FeedbackPeriodToolbar.tsx";
import { FeedbackFilterBar } from "./FeedbackFilterBar.tsx";

export function FeedbackFiltersHeading() {
    const [scope, setScope] = useFeedbackScope();
    const [period, setPeriod] = useFeedbackPeriod(scope);
    const scopeClient = useAnalyticsScopeClient();
    const { trackTimeRangeApplied, trackAudienceScopeChanged } = useAnalyticsTracking();

    const { data: previews, isPending } = useQuery(createQueryOptions(scopeClient.getPreviews.queryOptions(), { throwOnError: false }));

    const handlePeriodChange = useCallback(
        (next: FeedbackPeriod) => {
            setPeriod(next);
            trackTimeRangeApplied(next.kind === "preset" ? next.timeFrame : next.kind);
        },
        [setPeriod, trackTimeRangeApplied]
    );

    const handleScopeChange = useCallback(
        (next: ReportingScope) => {
            void setScope(next);
            trackAudienceScopeChanged(next);
        },
        [setScope, trackAudienceScopeChanged]
    );

    return (
        <ScopeSelector
            scope={scope}
            onScopeChange={handleScopeChange}
            previews={previews}
            isPreviewsPending={isPending}
            trailingContent={
                <div className="flex flex-wrap items-center gap-2">
                    <FeedbackPeriodToolbar period={period} onPeriodChange={handlePeriodChange} dataFloor={getFeedbackDataFloor(scope)} />
                    <FeedbackFilterBar />
                </div>
            }
        />
    );
}
